import React, { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { IGoal } from "../../../../types/dashboard";
import { getClientGoals, createGoal, updateGoal, deleteGoal, updateGoalKeepAlive } from "../../../../api/goalApi";
import { GoalCard } from "./GoalCard";
import { GoalModal } from "./GoalModal";

import './GoalsWidget.css';

export const GoalsWidget = () => {
    const { translate } = useContext(AppContext);
    const dashboardContext = useContext(DashboardContext);
    const { selectedClient } = dashboardContext || {};
    
    const [goals, setGoals] = useState<IGoal[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingGoal, setEditingGoal] = useState<IGoal | null>(null);
    const [error, setError] = useState<string>('');
    
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pendingChangesRef = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        if (selectedClient?.id) {
            loadGoals();
        } else {
            setGoals([]);
            setError('');
        }
    }, [selectedClient?.id]);

    useEffect(() => {
        const savePendingChanges = async () => {
            if (pendingChangesRef.current.size > 0) {
                const changes = Array.from(pendingChangesRef.current.entries());
                pendingChangesRef.current.clear();
                
                for (const [goalId, startDate] of changes) {
                    try {
                        await updateGoal(goalId, { startDate });
                    } catch (err) {
                        console.error('Failed to save pending goal change:', err);
                    }
                }
            }
        };

        const savePendingChangesKeepAlive = () => {
            if (pendingChangesRef.current.size > 0) {
                const changes = Array.from(pendingChangesRef.current.entries());
                pendingChangesRef.current.clear();
                
                for (const [goalId, startDate] of changes) {
                    updateGoalKeepAlive(goalId, { startDate });
                }
            }
        };

        const handleBeforeUnload = () => {
            savePendingChangesKeepAlive();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            savePendingChangesKeepAlive();
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const loadGoals = async () => {
        if (!selectedClient?.id) return;
        
        setLoading(true);
        setError('');
        try {
            const response = await getClientGoals(selectedClient.id);
            setGoals(Array.isArray(response) ? response : []);
            setError(''); // Clear error on successful load
        } catch (err) {
            console.error('Failed to load goals:', err);
            const errorMsg = translate('goals_load_error') || 'Failed to load goals';
            setError(errorMsg);
            setGoals([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddGoal = () => {
        setEditingGoal(null);
        setShowModal(true);
    };

    const handleEditGoal = (goal: IGoal) => {
        setEditingGoal(goal);
        setShowModal(true);
    };

    const handleSaveGoal = async (goalData: Partial<IGoal>) => {
        if (!selectedClient?.id) return;

        try {
            if (editingGoal?.id) {
                // Update existing goal
                await updateGoal(editingGoal.id, goalData);
            } else {
                // Create new goal
                await createGoal({
                    ...goalData,
                    clientId: selectedClient.id
                } as IGoal);
            }
            setError(''); // Clear any previous errors on success
            await loadGoals();
            setShowModal(false);
            setEditingGoal(null);
        } catch (err) {
            console.error('Failed to save goal:', err);
            const errorMsg = translate('goals_save_error') || 'Failed to save goal';
            setError(errorMsg);
            alert(errorMsg);
        }
    };

    const handleDeleteGoal = async (goalId: string) => {
        try {
            await deleteGoal(goalId);
            setError('');
            await loadGoals();
        } catch (err) {
            console.error('Failed to delete goal:', err);
            const errorMsg = translate('goals_delete_error') || 'Failed to delete goal';
            setError(errorMsg);
        }
    };

    const handleResetGoal = async (goal: IGoal) => {
        try {
            await updateGoal(goal.id!, { startDate: new Date().toISOString() });
            setError('');
            await loadGoals();
        } catch (err) {
            console.error('Failed to reset goal:', err);
            const errorMsg = translate('goals_save_error') || 'Failed to save goal';
            setError(errorMsg);
        }
    };

    const handleAdjustDays = (goal: IGoal, newDays: number) => {
        const now = new Date();
        const newStartDate = new Date(now.getTime() - (newDays * 24 * 60 * 60 * 1000));
        const newStartDateISO = newStartDate.toISOString();
        
        setGoals(prevGoals => 
            prevGoals.map(g => 
                g.id === goal.id 
                    ? { ...g, startDate: newStartDateISO }
                    : g
            )
        );
        
        pendingChangesRef.current.set(goal.id!, newStartDateISO);
        
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        
        saveTimerRef.current = setTimeout(async () => {
            if (pendingChangesRef.current.size > 0) {
                const changes = Array.from(pendingChangesRef.current.entries());
                
                for (const [goalId, startDate] of changes) {
                    try {
                        await updateGoal(goalId, { startDate });
                        if (pendingChangesRef.current.get(goalId) === startDate) {
                            pendingChangesRef.current.delete(goalId);
                        }
                        setError('');
                    } catch (err) {
                        console.error('Failed to save goal changes:', err);
                        const errorMsg = translate('goals_save_error') || 'Failed to save goal';
                        setError(errorMsg);
                    }
                }
            }
        }, 10000);
    };

    if (!selectedClient) {
        return (
            <div className="dashboard_main_block dashboard_main_goals">
                <div className="dashboard_main_block_head">
                    <div className="dashboard_main_block_title">
                        {translate('goals_title') || 'Goals'}
                    </div>
                </div>
                <div className="goals_widget_empty">
                    {translate('goals_select_client') || 'Select a client to view goals'}
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard_main_block dashboard_main_goals">
            <div className="dashboard_main_block_head">
                <div className="dashboard_main_block_title">
                    {translate('goals_title') || 'Goals'}
                </div>
                <div className="dashboard_main_block_head_right">
                    <div className="goals_widget_add_wrapper" onClick={handleAddGoal}>
                        <div className="goals_widget_add_icon"></div>
                        <span className="goals_widget_add_text">
                            {translate('goals_add') || 'Add goal'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="goals_widget_content">
                {loading ? (
                    <div className="goals_widget_loading">
                        {translate('common_loading') || 'Loading...'}
                    </div>
                ) : goals.length === 0 ? (
                    <div className="goals_widget_empty">
                        {translate('goals_empty') || 'No goals yet. Click "Add goal" to create one.'}
                    </div>
                ) : (
                    <div className="goals_widget_grid">
                        {goals.map((goal, index) => (
                            <GoalCard
                                key={goal.id}
                                goal={goal}
                                colorIndex={index}
                                onEdit={() => handleEditGoal(goal)}
                                onDelete={() => handleDeleteGoal(goal.id!)}
                                onReset={() => handleResetGoal(goal)}
                                onAdjustDays={(newDays) => handleAdjustDays(goal, newDays)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <GoalModal
                    goal={editingGoal}
                    onSave={handleSaveGoal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingGoal(null);
                    }}
                />
            )}
        </div>
    );
};
